import { useLazyQuery } from "@apollo/client";
import { capitaliseFirstLetter } from "@shared/translations/src/format";
import { createColumnHelper } from "@tanstack/react-table";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { usePrevious } from "react-use";
import { WeightedAverages } from "../WeightedAverages";
import { useTableSorting } from "@/components/DataTable/useTableSorting";
import { Pagination } from "@/components/Pagination";
import { pageInfoFragment, type ResetPagination, usePagination } from "@/components/Pagination/usePagination";
import { Table } from "@/components/Table";
import { Heading2 } from "@/components/Typography";
import { Button } from "@/components/ui/button";
import { ToastAction } from "@/components/ui/toast";
import { graphql, type VariablesOf, type ResultOf } from "@/graphql";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { useToast } from "@/hooks/use-toast";
import { removeNothings } from "@/lib/utils/arrays";
import { formatDateRangeBoundary, toDisplayDate } from "@/lib/utils/dates";
import { getErrorMessage } from "@/lib/utils/errors";
import { formatCurrency, formatWeight, formatMetricValue } from "@/lib/utils/formatting";
import { isValidStateCode, STATE_DATA } from "@/lib/utils/states";
import { type BenchmarkFilters } from "@/routes/(authed)/_layout.benchmarks";

export type SupplyChainLot = NonNullable<
  NonNullable<NonNullable<ResultOf<typeof supplyChainLotQuery>["supplyChainLots"]>["edges"]>[number]
>["node"];

const { accessor } = createColumnHelper<SupplyChainLot>();

type SortingField = NonNullable<NonNullable<VariablesOf<typeof supplyChainLotQuery>["sortBy"]>[0]>["field"];

interface LotsTableProps {
  tableId: string;
  status: "OPEN" | "CLOSED";
  filters: BenchmarkFilters;
}

export interface LotsTableHandle {
  resetPagination: ResetPagination;
}

export const LotsTable = forwardRef<LotsTableHandle, LotsTableProps>(({ tableId, status, filters }, ref) => {
  const {
    lotId,
    sexClass,
    animalBreedIds,
    lotDestinationIds,
    lotSourceIds,
    sourceStateCodes,
    dateClosedAfter,
    dateClosedBefore,
    dateOpenedAfter,
    dateOpenedBefore,
    feedyardIds,
    weightAverageInMin,
    weightAverageInMax,
    destinationCities,
    destinationStateCodes,
    sourceCities,
  } = filters;

  const [getSupplyChainLots, { loading, data, error, refetch }] = useLazyQuery(supplyChainLotQuery, {
    fetchPolicy: "cache-and-network",
  });
  const { toast } = useToast();
  const [isExpanded, setIsExpanded] = useState<boolean>(status === "CLOSED");
  const containerRef = useRef<HTMLDivElement>(null);
  const animating = useRef(false);
  const { activeBusinessUnitId } = useActiveBusinessUnit();

  const { sortBy, onSortingChange, sorting } = useTableSorting<SortingField>({ id: "dateOpened", desc: true });

  const pagination = usePagination({
    totalCount: data?.supplyChainLots?.numSupplyChainLots || 0,
    pageInfo: data?.supplyChainLots?.pageInfo,
    loading,
  });

  const lots = removeNothings(
    data?.supplyChainLots?.edges.map((edge) => {
      return edge?.node;
    }) || [],
  );

  const {
    paginationQueryVariables: { first, last, after, before },
  } = pagination;

  useEffect(() => {
    const variables: VariablesOf<typeof supplyChainLotQuery> = {
      first,
      last,
      after,
      before,
      sortBy,
      sexClass: sexClass?.length ? sexClass : undefined,
      animalBreeds: animalBreedIds?.length ? animalBreedIds : undefined,
      destinationPartners: lotDestinationIds?.length ? lotDestinationIds : undefined,
      destinationCities: destinationCities?.length ? destinationCities : undefined,
      destinationStates: destinationStateCodes?.length ? destinationStateCodes : undefined,
      referenceSearch: lotId || undefined,
      sourcePartners: lotSourceIds?.length ? lotSourceIds : undefined,
      sourceStates: sourceStateCodes?.length ? sourceStateCodes : undefined,
      sourceCities: sourceCities?.length ? sourceCities : undefined,
      groupBusinessUnits: feedyardIds?.length ? feedyardIds : undefined,
      dateOpenedAfter: formatDateRangeBoundary.start(dateOpenedAfter),
      dateOpenedBefore: formatDateRangeBoundary.end(dateOpenedBefore),
      dateClosedAfter: formatDateRangeBoundary.start(dateClosedAfter),
      dateClosedBefore: formatDateRangeBoundary.end(dateClosedBefore),
      status: [status],
      weightAverageInMin: weightAverageInMin || undefined,
      weightAverageInMax: weightAverageInMax || undefined,
      businessUnitIds: activeBusinessUnitId ? [activeBusinessUnitId] : undefined,
    };

    const fetchSupplyChainLots = async () => {
      await getSupplyChainLots({
        variables,
      });
    };

    fetchSupplyChainLots().catch((error) => {
      toast({
        variant: "destructive",
        title: "Uh oh! Something went wrong.",
        description: getErrorMessage(error) || "There was a problem with your request.",
        action: (
          <ToastAction onClick={() => refetch()} altText="Try again">
            Try again
          </ToastAction>
        ),
      });
    });
  }, [
    activeBusinessUnitId,
    after,
    animalBreedIds,
    before,
    dateClosedAfter,
    dateClosedBefore,
    dateOpenedAfter,
    dateOpenedBefore,
    destinationCities,
    destinationStateCodes,
    feedyardIds,
    first,
    getSupplyChainLots,
    last,
    lotDestinationIds,
    lotId,
    lotSourceIds,
    refetch,
    sexClass,
    sortBy,
    sourceCities,
    sourceStateCodes,
    status,
    toast,
    weightAverageInMax,
    weightAverageInMin,
  ]);

  useImperativeHandle(ref, () => ({
    resetPagination: pagination.controls.handleResetPagination,
  }));

  const prevDisplayNumLots = usePrevious(data?.supplyChainLots?.numSupplyChainLots);
  const displayNumLots =
    loading && prevDisplayNumLots ? prevDisplayNumLots : data?.supplyChainLots?.numSupplyChainLots || 0;

  /**
   * Uses Web Animations API to ensure we only scroll after the grid animation completes.
   * This is more reliable than setTimeout as it waits for actual animation completion
   * rather than assuming a fixed duration.
   */
  useEffect(() => {
    const checkAnimations = async () => {
      if (containerRef.current) {
        animating.current = true;
        const animations = containerRef.current.getAnimations({ subtree: true });
        const promises = animations.map((animation) => animation.finished);
        await Promise.all(promises);

        if (isExpanded && status === "OPEN") {
          containerRef.current.scrollIntoView({ behavior: "smooth" });
        }
        animating.current = false;
      }
    };

    void checkAnimations();
  }, [isExpanded, status]);

  return (
    <>
      <div className="flex items-center p-4">
        <Heading2>
          {status === "CLOSED" ? "Closed" : "Open"} Lots ({displayNumLots})
        </Heading2>
        <Button
          variant="secondary"
          onClick={() => {
            if (!animating.current) {
              setIsExpanded((prev) => !prev);
            }
          }}
          className="ml-2 p-2"
          disabled={animating.current}
        >
          {isExpanded ? (
            <>
              <ChevronUpIcon className="h-4 w-4" /> Hide
            </>
          ) : (
            <>
              <ChevronDownIcon className="h-4 w-4" /> Show
            </>
          )}
        </Button>
      </div>
      {status === "CLOSED" && isExpanded && <WeightedAverages />}
      <div
        ref={containerRef}
        className={`
              grid
              border-b border-solid border-border
              transition-[grid-template-rows] duration-300 ease-in-out
              ${isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}
            `}
      >
        <div className="overflow-hidden">
          <Table.Root
            data={lots}
            columns={columns}
            loading={loading && !data}
            error={error?.message}
            tableId={tableId}
            sortConfig={{ onSortingChange, sorting }}
          >
            <Table.Header>
              <Table.ColumnToggle />
            </Table.Header>
            <Table.Body pageSize={Number(pagination.paginationInfo.pageSize)} />
            <Table.Footer className="flex justify-end">
              <Pagination pagination={pagination} />
            </Table.Footer>
          </Table.Root>
        </div>
      </div>
    </>
  );
});

export const supplyChainLotQuery = graphql(
  `
    query SupplyChainLots(
      $first: Int
      $last: Int
      $before: String
      $after: String
      $sortBy: [SupplyChainLotsColumnSort]
      $sexClass: [SupplyChainSexClassEnumType]
      $animalBreeds: [UUID]
      $dateOpenedBefore: DateTime
      $dateOpenedAfter: DateTime
      $dateClosedBefore: DateTime
      $dateClosedAfter: DateTime
      $sourcePartners: [UUID!]
      $sourceCities: [String!]
      $sourceStates: [String]
      $destinationPartners: [UUID!]
      $destinationCities: [String!]
      $groupBusinessUnits: [ID]
      $destinationStates: [String!]
      $status: [SupplyChainStatusEnumType]
      $weightAverageInMin: Decimal
      $weightAverageInMax: Decimal
      $businessUnitIds: [ID]
      $referenceSearch: String
    ) {
      supplyChainLots(
        first: $first
        last: $last
        before: $before
        after: $after
        sortBy: $sortBy
        sexClass: $sexClass
        animalBreeds: $animalBreeds
        dateOpenedBefore: $dateOpenedBefore
        dateOpenedAfter: $dateOpenedAfter
        dateClosedBefore: $dateClosedBefore
        dateClosedAfter: $dateClosedAfter
        sourcePartners: $sourcePartners
        sourceCities: $sourceCities
        sourceStates: $sourceStates
        destinationPartners: $destinationPartners
        destinationCities: $destinationCities
        groupBusinessUnits: $groupBusinessUnits
        destinationStates: $destinationStates
        status: $status
        weightAverageInMin: $weightAverageInMin
        weightAverageInMax: $weightAverageInMax
        owners: $businessUnitIds
        referenceSearch: $referenceSearch
      ) {
        numSupplyChainLots
        pageInfo {
          ...PageInfo
        }
        edges {
          node {
            id
            group {
              id
              name
              businessUnit {
                id
                name
              }
            }
            dateClosed
            dateOpened
            reference
            animalBreeds {
              id
              name
            }
            sexClass
            numTotalIn
            numTotalOut
            numTotalDead
            numTotalRemoved
            status
            purchasePrice
            salePrice
            feedCost
            nonFeedCost
            weightTotalIn
            weightTotalOut
            weightTotalFeedIntake
            feedConversion
            costOfGain
            sourcePartner {
              id
              businessName
            }
            sourceLocation {
              id
              address {
                id
                city
                state
              }
            }
            destinationPartner {
              id
              businessName
            }
            destinationLocation {
              id
              address {
                id
                city
                state
              }
            }
            mortalityRate
            primeFraction
            selectFraction
            choiceFraction
            daysOnFeed
            averageDailyGain
            weightAverageIn
            weightAverageOut
            feedConversion
            costOfGain
            sortNote
          }
        }
      }
    }
  `,
  [pageInfoFragment],
);

const columns = [
  accessor((row) => row?.group?.businessUnit.name, {
    id: "feedyard",
    header: "Feedyard",
    cell: (info) => info.getValue() || "\u2014",
  }),
  accessor((row) => row?.sourcePartner?.businessName, {
    id: "sourceName" satisfies SortingField,
    header: "Source",
    cell: ({ row: { original } }) => {
      const value = original?.sourcePartner?.businessName;
      return value?.trim() || "\u2014";
    },
    enableSorting: true,
  }),
  accessor((row) => row?.sourceLocation?.address?.city, {
    id: "sourceCity" satisfies SortingField,
    header: "Source City",
    cell: ({ row: { original } }) => {
      const value = original?.sourceLocation?.address?.city;
      return value?.trim() || "\u2014";
    },
    enableSorting: true,
  }),
  accessor((row) => row?.sourceLocation?.address?.state, {
    id: "sourceState" satisfies SortingField,
    header: "Source State",
    cell: ({ row: { original } }) => {
      const stateCode = original?.sourceLocation?.address?.state?.trim();
      return stateCode && isValidStateCode(stateCode) ? `${STATE_DATA[stateCode]} (${stateCode})` : "\u2014";
    },
    enableSorting: true,
  }),
  accessor((row) => row?.destinationPartner?.businessName, {
    id: "destinationName" satisfies SortingField,
    header: "Destination",
    cell: ({ row: { original } }) => {
      const value = original?.destinationPartner?.businessName;
      return value?.trim() || "\u2014";
    },
    enableSorting: true,
  }),
  accessor((row) => row?.destinationLocation?.address?.city, {
    id: "destinationCity" satisfies SortingField,
    header: "Destination City",
    cell: ({ row: { original } }) => {
      const value = original?.destinationLocation?.address?.city;
      return value?.trim() || "\u2014";
    },
    enableSorting: true,
  }),
  accessor((row) => row?.destinationLocation?.address?.state, {
    id: "destinationState" satisfies SortingField,
    header: "Destination State",
    cell: ({ row: { original } }) => {
      const stateCode = original?.destinationLocation?.address?.state?.trim();
      return stateCode && isValidStateCode(stateCode) ? `${STATE_DATA[stateCode]} (${stateCode})` : "\u2014";
    },
    enableSorting: true,
  }),
  accessor("reference", {
    id: "reference" satisfies SortingField,
    header: "Lot ID",
    cell: (info) => info.getValue(),
    enableSorting: true,
  }),
  accessor("sexClass", {
    id: "sexClass",
    header: "Sex",
    cell: (info) => capitaliseFirstLetter(info.getValue()?.toLowerCase()) || "\u2014",
  }),
  accessor("animalBreeds", {
    id: "breed",
    header: "Breed",
    cell: (info) => {
      const breeds = info.getValue() || [];
      if (breeds.length === 0) return "\u2014";
      return breeds.map((breed) => breed?.name).join(", ");
    },
  }),
  accessor("dateOpened", {
    id: "dateOpened" satisfies SortingField,
    header: "Opened",
    cell: (info) => {
      const date = info.getValue();
      const formattedDate = toDisplayDate(date || undefined);
      return formattedDate || "\u2014";
    },
    enableSorting: true,
  }),
  accessor("dateClosed", {
    id: "dateClosed" satisfies SortingField,
    header: "Closed",
    cell: (info) => {
      const date = info.getValue();
      const formattedDate = toDisplayDate(date || undefined);
      return formattedDate || "\u2014";
    },
    enableSorting: true,
  }),
  accessor("numTotalIn", {
    id: "numTotalIn" satisfies SortingField,
    header: "Head In",
    cell: (info) => info.getValue() || "\u2014",
    enableSorting: true,
  }),
  accessor("numTotalOut", {
    id: "numTotalOut" satisfies SortingField,
    header: "Head Out",
    cell: (info) => info.getValue() || "\u2014",
    enableSorting: true,
  }),
  accessor("numTotalDead", {
    id: "numTotalDead" satisfies SortingField,
    header: "Head Dead",
    cell: (info) => info.getValue() || "\u2014",
    enableSorting: true,
  }),
  accessor("numTotalRemoved", {
    id: "numTotalRemoved" satisfies SortingField,
    header: "Head Removed",
    cell: (info) => info.getValue() || "\u2014",
    enableSorting: true,
  }),
  accessor("salePrice", {
    id: "salePrice" satisfies SortingField,
    header: "Sale Price",
    cell: (info) => formatCurrency(info.getValue()),
    enableSorting: true,
  }),
  accessor("purchasePrice", {
    id: "purchasePrice" satisfies SortingField,
    header: "Purchase Price",
    cell: (info) => formatCurrency(info.getValue()),
    enableSorting: true,
  }),
  accessor("feedCost", {
    id: "feedCost" satisfies SortingField,
    header: "Feed Cost",
    cell: (info) => formatCurrency(info.getValue()),
    enableSorting: true,
  }),
  accessor("nonFeedCost", {
    id: "nonFeedCost" satisfies SortingField,
    header: "Non-Feed Cost",
    cell: (info) => formatCurrency(info.getValue()),
    enableSorting: true,
  }),
  accessor("weightTotalIn", {
    id: "weightTotalIn" satisfies SortingField,
    header: "Total Weight In",
    cell: (info) => formatWeight(info.getValue()),
    enableSorting: true,
  }),
  accessor("weightTotalOut", {
    id: "weightTotalOut" satisfies SortingField,
    header: "Total Weight Out",
    cell: (info) => formatWeight(info.getValue()),
    enableSorting: true,
  }),
  accessor("weightAverageIn", {
    id: "weightAverageIn" satisfies SortingField,
    header: "Avg. Weight In",
    cell: (info) => formatWeight(info.getValue()),
    enableSorting: true,
  }),
  accessor("weightAverageOut", {
    id: "weightAverageOut" satisfies SortingField,
    header: "Avg. Weight Out",
    cell: (info) => formatWeight(info.getValue()),
    enableSorting: true,
  }),
  accessor("weightTotalFeedIntake", {
    id: "weightTotalFeedIntake" satisfies SortingField,
    header: "Total Feed Intake",
    cell: (info) => formatWeight(info.getValue()),
    enableSorting: true,
  }),
  accessor("primeFraction", {
    id: "primeFraction" satisfies SortingField,
    header: "Prime",
    cell: (info) => formatMetricValue({ value: info.getValue(), metricType: "percentage", fallbackLabel: "\u2014" }),
    enableSorting: true,
  }),
  accessor("selectFraction", {
    id: "selectFraction" satisfies SortingField,
    header: "Select",
    cell: (info) => formatMetricValue({ value: info.getValue(), metricType: "percentage", fallbackLabel: "\u2014" }),
    enableSorting: true,
  }),
  accessor("choiceFraction", {
    id: "choiceFraction" satisfies SortingField,
    header: "Choice",
    cell: (info) => formatMetricValue({ value: info.getValue(), metricType: "percentage", fallbackLabel: "\u2014" }),
    enableSorting: true,
  }),
  accessor("mortalityRate", {
    id: "mortalityRate" satisfies SortingField,
    header: "Mortality Rate",
    cell: (info) => formatMetricValue({ value: info.getValue(), metricType: "percentage", fallbackLabel: "\u2014" }),
    enableSorting: true,
  }),
  accessor("daysOnFeed", {
    id: "daysOnFeed" satisfies SortingField,
    header: "Days on Feed",
    cell: (info) => info.getValue() || "\u2014",
    enableSorting: true,
  }),
  accessor("averageDailyGain", {
    id: "averageDailyGain" satisfies SortingField,
    header: "ADG",
    cell: (info) => formatMetricValue({ value: info.getValue(), fallbackLabel: "\u2014", metricType: "weight" }),
    enableSorting: true,
  }),
  accessor("feedConversion", {
    id: "feedConversion" satisfies SortingField,
    header: "Feed Conversion",
    cell: (info) => formatMetricValue({ value: info.getValue(), fallbackLabel: "\u2014" }),
    enableSorting: true,
  }),
  accessor("costOfGain", {
    id: "costOfGain" satisfies SortingField,
    header: "Cost of Gain",
    cell: (info) => formatMetricValue({ value: info.getValue(), fallbackLabel: "\u2014", metricType: "currency" }),
    enableSorting: true,
  }),
  accessor("sortNote", {
    id: "sortNote",
    header: "Sort",
    cell: (info) => info.getValue() || "\u2014",
  }),
];
